import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { dialerSessionApi, type DialerSession, type DialerSessionLead } from "../services/dialerSessionService";

const QUERY_KEY = "dialer-sessions";

export function useDialerSessionList(enabled = true, page = 1, limit = 50) {
  return useQuery({
    queryKey: [QUERY_KEY, "list", page, limit],
    queryFn: async () => {
      const res = await dialerSessionApi.listSessions(page, limit);
      return res.data?.data || { sessions: [], total: 0, page: 1 };
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useDialerSessionDetail(sessionId: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY, "detail", sessionId],
    queryFn: async () => {
      if (!sessionId) return null;
      const res = await dialerSessionApi.getSession(sessionId);
      return res.data?.data || null;
    },
    enabled: !!sessionId,
  });
}

export function useCreateDialerSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { dialMode: "power" | "multiline"; leads: DialerSessionLead[]; source?: "manual" | "dialiq" }) => {
      const res = await dialerSessionApi.createSession(data);
      return res.data?.data?.id || null;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useCompleteDialerSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      await dialerSessionApi.completeSession(id, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useSaveDialerCallResult() {
  return useMutation({
    mutationFn: async ({ sessionId, data }: { sessionId: string; data: Record<string, unknown> }) => {
      await dialerSessionApi.saveCallResult(sessionId, data);
    },
  });
}

export function useUpdateDialerProgress() {
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      await dialerSessionApi.updateProgress(id, data);
    },
  });
}
